import {CM_LOGGER_TEMPLATES} from "@app/common/services/logging"
import {Component, computed, DestroyRef, inject, Injector, input, OnDestroy, OnInit, signal, ChangeDetectionStrategy} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {MatDialog, MatDialogRef} from "@angular/material/dialog"
import {MatMenuModule} from "@angular/material/menu"
import {MatProgressBarModule} from "@angular/material/progress-bar"
import {MatTooltipModule} from "@angular/material/tooltip"
import {DialogComponent} from "@app/common/components/dialogs/dialog/dialog.component"
import {
    BatchOperationDialogComponent,
    BatchOperationDialogResult,
} from "@app/template-editor/components/batch-operation-dialog/batch-operation-dialog.component"
import {PermissionsService} from "@app/common/services/permissions/permissions.service"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {RenderingService} from "@app/common/services/rendering/rendering.service"
import {DIALOG_DEFAULT_WIDTH} from "@app/template-editor/helpers/constants"
import {
    createRenderJob,
    deleteRenderJob,
    getAllJobAssignments,
    getAssignmentKey,
    getConfigurationString,
    getJobAssignments,
    JobData,
    parseAssignmentKey,
    UnassignedJobData,
    createBatchExportJobGraph,
    isJobError,
    isJobPending,
    BatchExportMediaType,
} from "@app/template-editor/helpers/template-jobs"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {ConfigInfo, Parameters, SceneNodes} from "@cm/template-nodes"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {
    GetJobDetailsForTemplateImageViewerGQL,
    JobAssignmentDetailsForTemplateImageViewerFragment,
} from "@template-editor/components/template-image-viewer/template-image-viewer.component.generated"
import {TemplateJobManagerService} from "@template-editor/services/template-job-manager.service"
import {combineLatest, concatMap, firstValueFrom, from, map, of, switchMap, tap, catchError, defer, interval, startWith, Subject, takeUntil} from "rxjs"
import {ButtonComponent} from "@app/common/components/buttons/button/button.component"
import {ListItemComponent} from "@app/common/components/item/list-item/list-item.component"
import {TemplateJobIconComponent} from "@app/template-editor/components/template-job-icon/template-job-icon.component"
import {ConfigurationInfo, gatherAllVariations} from "@app/template-editor/helpers/all-variations"
import {RunBackendJobOperation} from "@app/common/models/background-operation/run-backend-job-operation"
import {FilesService} from "@app/common/services/files/files.service"
import {BackgroundOperationService} from "@app/platform/services/background-operation/background-operation.service"
import {JobsAllVariationsGQL} from "@app/template-editor/components/template-all-variations/template-all-variations.generated"
import {JobState, JobType} from "@generated"
import {environment} from "@environment"
import {LoadingSpinnerIconComponent} from "@app/common/components/progress"

type ConfigurationData = Omit<ConfigurationInfo, "scene"> & {
    renderSettings: SceneNodes.RenderSettings | undefined
    postProcessingSettings: SceneNodes.RenderPostProcessingSettings | undefined
    render?: JobData
}

type RenderJobAssignment = {
    type: "render"
    configurationParameters: Parameters | undefined
    hash: string | undefined
    jobAssignment: JobAssignmentDetailsForTemplateImageViewerFragment & {assignmentKey: string}
}

function isJobCompleted(job: JobData) {
    return !isJobPending(job) && !isJobError(job)
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-all-variations",
    imports: [MatProgressBarModule, MatTooltipModule, ListItemComponent, TemplateJobIconComponent, ButtonComponent, MatMenuModule, LoadingSpinnerIconComponent],
    providers: [SceneManagerService],
    templateUrl: "./template-all-variations.component.html",
    styleUrl: "./template-all-variations.component.scss",
})
export class TemplateAllVariationsComponent implements OnInit, OnDestroy {
    private readonly logger = inject(CM_LOGGER_TEMPLATES)

    readonly $sceneManagerService = input.required<SceneManagerService>({alias: "sceneManagerService"})
    readonly $title = input<string | undefined>(undefined, {alias: "title"})

    private readonly localSceneManagerService = inject(SceneManagerService)
    private readonly destroyRef = inject(DestroyRef)
    private readonly injector = inject(Injector)
    private readonly matDialog = inject(MatDialog)
    private readonly notifications = inject(NotificationsService)
    private readonly renderingService = inject(RenderingService)
    private readonly templateJobManagerService = inject(TemplateJobManagerService)
    private readonly backgroundOperationService = inject(BackgroundOperationService)
    private readonly jobsAllVariationsGql = inject(JobsAllVariationsGQL)
    private readonly permission = inject(PermissionsService)
    readonly $can = this.permission.$to

    readonly jobGql = inject(GetJobDetailsForTemplateImageViewerGQL)

    private readonly $_progress = signal<number>(0)
    $progress = this.$_progress.asReadonly()

    // Persistent cancellation subject: we only emit, never complete, so it can be reused
    private readonly cancelSubject = new Subject<void>()

    cancelCurrentOperation() {
        if (this.$state() === "loading") {
            this.cancelSubject.next()
            this.$state.set("loaded")
            this.$_progress.set(0)
        }
    }

    readonly $state = signal<"loading" | "loaded" | "error">("loading")

    readonly $fallbackPostProcessingSettings = computed(() => this.$sceneManagerService().$scene().find(SceneNodes.RenderPostProcessingSettings.is))

    private readonly $unassignedRenderJobs = computed(() =>
        this.$allRenderJobAssignments().filter(({jobAssignment}) => {
            return !this.$allVariations().some((variation) => variation.render?.id === jobAssignment.job.id)
        }),
    )

    readonly $renderAllDisabled = computed(() => {
        return this.$state() !== "loaded" || this.$allVariations().every((variation) => variation.render !== undefined)
    })
    readonly $downloadAllDisabled = computed(() => {
        if (this.$state() !== "loaded") return true
        const hasCompletedAssigned = this.$allVariations().some((variation) => variation.render !== undefined && isJobCompleted(variation.render))
        const hasCompletedUnassigned = this.$unassignedRenderJobs().some(({jobAssignment}) => isJobCompleted(jobAssignment.job))
        return !hasCompletedAssigned && !hasCompletedUnassigned
    })
    readonly $deleteAllDisabled = computed(() => {
        if (this.$state() !== "loaded") return true
        return !this.$allVariations().some((variation) => variation.render !== undefined) && this.$unassignedRenderJobs().length === 0
    })

    readonly $allVariations = signal<ConfigurationData[]>([])
    readonly $allConfigInfo = signal<ConfigInfo[]>([])
    private readonly $allRenderJobAssignments = signal<RenderJobAssignment[]>([])
    readonly $numRendering = computed(() => {
        return this.$allRenderJobAssignments().filter(({jobAssignment}) => isJobPending(jobAssignment.job)).length
    })
    readonly $numRendered = computed(() => {
        return this.$allRenderJobAssignments().filter(({jobAssignment}) => isJobCompleted(jobAssignment.job)).length
    })
    readonly $numFailed = computed(() => {
        return this.$allRenderJobAssignments().filter(({jobAssignment}) => isJobError(jobAssignment.job)).length
    })

    $unassignedJobs = this.templateJobManagerService.$unassignedJobs
    $selectedUnassignedJob = this.templateJobManagerService.$selectedUnassignedJob

    readonly $currentLocalConfigurationHash = computed(() => {
        return this.$sceneManagerService().$currentLocalConfiguration().getHash()
    })

    async setConfiguration(configuration: ConfigurationData) {
        this.$sceneManagerService().$instanceParameters.set(configuration.parameters)

        const templateRevisionId = this.$sceneManagerService().$templateRevisionId()
        if (!templateRevisionId) return

        const configurationString = getConfigurationString(configuration.parameters)
        const [render] = await getJobAssignments(this.injector, templateRevisionId, [getAssignmentKey("render", configurationString)])

        this.$allVariations.update((variations) => variations.map((v) => (v.hash === configuration.hash ? {...configuration, render} : v)))
        if (!render) this.$allRenderJobAssignments.update((assignments) => assignments.filter((a) => a.hash !== configuration.hash))
        else this.$allRenderJobAssignments.update((assignments) => assignments.map((a) => (a.hash === configuration.hash ? {...a, job: render} : a)))

        this.$selectedUnassignedJob.set(undefined)
    }

    private displayName(configuration: Parameters | undefined) {
        const allConfigInfo = this.$allConfigInfo()

        const fallbackDisplayName = "Unknown config(s)"
        if (!configuration) return fallbackDisplayName

        const configNames: string[] = []
        Object.entries(configuration.parameters).forEach(([key, value]) => {
            const configName = allConfigInfo.find((x) => x.props.id === key)?.props.name ?? key
            const variantName = allConfigInfo.find((x) => x.props.id === key)?.props.variants.find((x) => x.id === value)?.name ?? value

            configNames.push(`${configName}: ${variantName}`)
        })

        return configNames.length > 0 ? configNames.join(", ") : fallbackDisplayName
    }

    private refreshUnassignedJobs() {
        this.$unassignedJobs.set(
            this.$unassignedRenderJobs().map(({type, configurationParameters, jobAssignment}) => {
                return {
                    job: jobAssignment.job,
                    displayName: this.displayName(configurationParameters),
                    type,
                }
            }),
        )
    }

    ngOnInit() {
        this.localSceneManagerService.$lodType.set("pathTraced")
        this.localSceneManagerService.$computeDummyReifiedMeshData.set(true)

        this.localSceneManagerService.$exposeClaimedSubTemplateInputs.set(true)

        const sceneManagerService = this.$sceneManagerService()

        const cancel$ = this.cancelSubject
        combineLatest([sceneManagerService.templateRevisionId$, sceneManagerService.defaultCustomerId$, sceneManagerService.templateGraph$])
            .pipe(
                tap(() => {
                    this.$allVariations.set([])
                    this.$_progress.set(0)

                    this.$state.set("loading")
                }),
                switchMap(([templateRevisionId, defaultCustomerId, templateGraph]) => {
                    this.localSceneManagerService.$templateRevisionId.set(templateRevisionId)
                    this.localSceneManagerService.$defaultCustomerId.set(defaultCustomerId)
                    const clonedTemplateGraph = templateGraph.clone({cloneSubNode: () => true})
                    this.localSceneManagerService.$templateGraph.set(clonedTemplateGraph)

                    const configInfoSideEffect = (configInfo: ConfigInfo) => {
                        this.$allConfigInfo.update((oldValue) => {
                            if (oldValue.some((x) => x.props.id === configInfo.props.id)) return oldValue
                            return [...oldValue, configInfo]
                        })
                    }

                    return gatherAllVariations(this.localSceneManagerService, "local", (progress) => this.$_progress.set(progress), configInfoSideEffect)
                }),
                map((variation) => {
                    if (variation === "done") return "done"
                    return {
                        ...variation,
                        render: this.$allRenderJobAssignments().find((assignment) => variation.hash === assignment.hash)?.jobAssignment.job,
                    }
                }),
                takeUntil(cancel$),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (variation) => {
                    if (variation === "done") {
                        this.logger.debug("Done gathering variations", this.$allVariations().length)
                        this.refreshUnassignedJobs()
                        this.$state.set("loaded")
                    } else {
                        this.logger.debug("Added variation to allVariations")
                        this.$allVariations.update((oldValue) => {
                            const {scene, ...variationWithoutScene} = variation
                            const renderSettings = scene.find(SceneNodes.RenderSettings.is)
                            const postProcessingSettings = scene.find(SceneNodes.RenderPostProcessingSettings.is)
                            const newValue = [...oldValue, {...variationWithoutScene, renderSettings, postProcessingSettings}]
                            newValue.sort((a, b) => a.name.localeCompare(b.name))
                            return newValue
                        })
                    }
                },
                complete: () => {
                    this.refreshUnassignedJobs()
                },
                error: (e) => {
                    this.$state.set("error")
                    this.logger.error("Error while gathering variations", e)
                },
            })

        this.localSceneManagerService.templateRevisionId$
            .pipe(
                switchMap((templateRevisionId) => {
                    if (!templateRevisionId) return of(null)
                    return interval(10000).pipe(
                        startWith(0), // run immediately, then every 10s
                        switchMap(() =>
                            from(getAllJobAssignments(this.injector, templateRevisionId)).pipe(
                                map((jobAssignments) => {
                                    return jobAssignments
                                        .filter((jobAssignment) => jobAssignment !== null)
                                        .map((jobAssignment) => {
                                            const parsed = jobAssignment.assignmentKey ? parseAssignmentKey(jobAssignment.assignmentKey) : undefined
                                            return {
                                                type: parsed?.type,
                                                configurationParameters: parsed?.configurationParameters,
                                                hash: parsed?.configurationParameters?.getHash(),
                                                jobAssignment,
                                            }
                                        })
                                        .filter((jobInfo): jobInfo is RenderJobAssignment => jobInfo.type === "render" && !!jobInfo.jobAssignment.assignmentKey)
                                }),
                            ),
                        ),
                    )
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((renderJobAssignments) => {
                if (!renderJobAssignments) {
                    this.$allRenderJobAssignments.set([])
                    return
                }
                this.$allRenderJobAssignments.set(renderJobAssignments)

                this.$allVariations.update((variations) => {
                    return variations.map((variation) => {
                        return {
                            ...variation,
                            render: renderJobAssignments.find((assignment) => variation.hash === assignment.hash)?.jobAssignment.job,
                        }
                    })
                })

                if (this.$state() !== "loading") {
                    this.refreshUnassignedJobs()
                }
            })

        this.$unassignedJobs.set([])
        this.$selectedUnassignedJob.set(undefined)
    }

    async renderAll() {
        const templateRevisionId = this.localSceneManagerService.$templateRevisionId()
        if (!templateRevisionId) throw Error("No template revision id set")

        const defaultCustomerId = this.localSceneManagerService.$defaultCustomerId()
        if (!defaultCustomerId) throw Error("No default customer id set")

        const runningJobs = await fetchThrowingErrors(this.jobsAllVariationsGql)({
            filter: {
                organizationLegacyId: {equals: defaultCustomerId},
                state: [JobState.Init, JobState.Runnable, JobState.Running],
            },
        })

        const variations = this.$allVariations().filter((variation) => variation.render === undefined)
        const allowedJobCount = Math.max(environment.rendering.maxConcurrentRenderJobs - runningJobs.jobs.length, 0)

        const dialogRef: MatDialogRef<DialogComponent, boolean> = this.matDialog.open(DialogComponent, {
            disableClose: false,
            width: DIALOG_DEFAULT_WIDTH,
            data: {
                title: "Submit renders",
                message:
                    variations.length <= allowedJobCount
                        ? `You are submitting <b>${variations.length}</b> render jobs.</br></br>Are you sure you want to continue?`
                        : `You are trying to submit <b>${variations.length}</b> render jobs, but only <b>${allowedJobCount}</b> more jobs can be submitted at this time due to system limits.</br></br>The first <b>${allowedJobCount}</b> jobs will be submitted. Are you sure you want to continue?`,
                confirmLabel: "Submit jobs",
                cancelLabel: "Cancel",
            },
        })

        variations.splice(allowedJobCount)

        const confirmed = await firstValueFrom(dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)))
        if (confirmed !== true) return

        let numSubmitted = 0
        let numFailed = 0
        this.$_progress.set(0)
        this.$state.set("loading")

        const cancel$ = this.cancelSubject
        from(variations)
            .pipe(
                concatMap((variation) => {
                    const variationString = getConfigurationString(variation.parameters)
                    return defer(() => {
                        this.localSceneManagerService.$instanceParameters.set(variation.parameters)
                        this.localSceneManagerService.compileTemplate()
                        return from(this.localSceneManagerService.sync(true)).pipe(
                            switchMap(() =>
                                from(
                                    createRenderJob(
                                        this.injector,
                                        this.renderingService,
                                        templateRevisionId,
                                        variationString,
                                        this.localSceneManagerService.$scene(),
                                        defaultCustomerId,
                                    ),
                                ),
                            ),
                            tap(([_, job]) => {
                                this.$allVariations.update((variations) => variations.map((v) => (v.hash === variation.hash ? {...variation, render: job} : v)))
                                numSubmitted += 1
                                this.$_progress.set(((numSubmitted + numFailed) / variations.length) * 100)
                            }),
                        )
                    }).pipe(
                        catchError((e) => {
                            this.logger.error("Error while submitting job", e)
                            numFailed += 1
                            this.$_progress.set(((numSubmitted + numFailed) / variations.length) * 100)
                            return of(null)
                        }),
                    )
                }),
                takeUntil(cancel$),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                complete: () => {
                    this.$state.set(numFailed === 0 ? "loaded" : "error")
                    this.notifications.showInfo(`Submitted ${numSubmitted} render jobs.${numFailed > 0 ? ` Failed to submit ${numFailed} jobs.` : ""}`)
                },
            })
    }

    async downloadAll(mediaType: BatchExportMediaType) {
        const variations = this.$allVariations().filter(
            (variation): variation is ConfigurationData & {render: JobData} => variation.render !== undefined && isJobCompleted(variation.render),
        )

        const templateRevisionId = this.localSceneManagerService.$templateRevisionId()
        if (!templateRevisionId) throw Error("No template revision id set")

        const defaultCustomerId = this.localSceneManagerService.$defaultCustomerId()
        if (!defaultCustomerId) throw Error("No default customer id set")

        const variationsData = variations.map((variation) => ({
            configurationString: getConfigurationString(variation.parameters),
            name: variation.name,
            postProcessingSettings: variation.postProcessingSettings,
        }))

        const unmatchedRenderJobs = this.$unassignedRenderJobs()
            .filter(({jobAssignment}) => isJobCompleted(jobAssignment.job))
            .map(({jobAssignment, configurationParameters}) => ({
                configurationString: jobAssignment.assignmentKey.replace("render:", ""),
                name: this.displayName(configurationParameters),
                postProcessingSettings: this.$fallbackPostProcessingSettings(),
            }))

        const dialogRef: MatDialogRef<BatchOperationDialogComponent, BatchOperationDialogResult | undefined> = this.matDialog.open(
            BatchOperationDialogComponent,
            {
                disableClose: false,
                width: DIALOG_DEFAULT_WIDTH,
                data: {
                    configurationCount: variationsData.length,
                    unassignedCount: unmatchedRenderJobs.length,
                    title: "Batch download renders",
                    description: "You are submitting batch download of rendered images for",
                    confirmLabel: "Submit job",
                },
            },
        )

        const result = await firstValueFrom(dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)))
        if (!result) return

        if (result.includeUnassigned) {
            variationsData.push(...unmatchedRenderJobs)
        }

        const exportName = this.$title() || `Template (${templateRevisionId})`

        const jobGraph = await createBatchExportJobGraph(this.injector, variationsData, mediaType, templateRevisionId, defaultCustomerId, exportName)

        const jobName = `Batch download generation for template ${templateRevisionId}`

        const onBackendJobCompleted = (jobData: JobData, setResult: (result: {url: string; title: string}) => void) => {
            const resultUrl = jobData.output?.downloadUrl
            const resultTitle = "Download link"

            if (!resultUrl) return
            setResult({url: resultUrl, title: resultTitle})
            FilesService.downloadFile(exportName, resultUrl)
        }

        const runBackendJobOperation = new RunBackendJobOperation(
            this.injector,
            "Download",
            `Preparing: ${exportName}`,
            {jobType: JobType.BatchDownload, name: jobName, graph: jobGraph, organizationLegacyId: defaultCustomerId},
            onBackendJobCompleted,
        )
        this.backgroundOperationService.registerBackgroundOperation(runBackendJobOperation)

        void runBackendJobOperation.submitJob()
    }

    async deleteAll() {
        const variations = this.$allVariations().filter((variation): variation is ConfigurationData & {render: JobData} => variation.render !== undefined)

        const unmatchedRenderJobs = this.$unassignedRenderJobs()

        const dialogRef: MatDialogRef<BatchOperationDialogComponent, BatchOperationDialogResult | undefined> = this.matDialog.open(
            BatchOperationDialogComponent,
            {
                disableClose: false,
                width: DIALOG_DEFAULT_WIDTH,
                data: {
                    configurationCount: variations.length,
                    unassignedCount: unmatchedRenderJobs.length,
                    title: "Delete renders",
                    description: "You are deleting images of",
                    confirmLabel: "Delete",
                    includeUnassignedByDefault: false,
                    destructive: true,
                },
            },
        )

        const result = await firstValueFrom(dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)))
        if (!result) return

        const templateRevisionId = this.localSceneManagerService.$templateRevisionId()
        if (!templateRevisionId) throw Error("No template revision id set")

        const jobsToDelete: {configurationString: string; jobId: string; hash?: string}[] = variations.map((variation) => ({
            configurationString: getConfigurationString(variation.parameters),
            jobId: variation.render.id,
            hash: variation.hash,
        }))

        if (result.includeUnassigned) {
            jobsToDelete.push(
                ...unmatchedRenderJobs.map(({jobAssignment}) => ({
                    configurationString: jobAssignment.assignmentKey.replace("render:", ""),
                    jobId: jobAssignment.job.id,
                })),
            )
        }

        let numDeleted = 0
        let numFailed = 0
        this.$_progress.set(0)
        this.$state.set("loading")

        const cancel$ = this.cancelSubject
        from(jobsToDelete)
            .pipe(
                concatMap((job) => {
                    return from(deleteRenderJob(this.injector, templateRevisionId, job.configurationString)).pipe(
                        tap(() => {
                            if (job.hash) {
                                this.$allVariations.update((variations) => variations.map((v) => (v.hash === job.hash ? {...v, render: undefined} : v)))
                            }
                            this.$allRenderJobAssignments.update((assignments) =>
                                assignments.filter((assignment) => assignment.jobAssignment.job.id !== job.jobId),
                            )
                            numDeleted += 1
                            this.$_progress.set(((numDeleted + numFailed) / jobsToDelete.length) * 100)
                        }),
                        catchError((e) => {
                            this.logger.error("Error while deleting job", e)
                            numFailed += 1
                            this.$_progress.set(((numDeleted + numFailed) / jobsToDelete.length) * 100)
                            return of(null)
                        }),
                    )
                }),
                takeUntil(cancel$),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                complete: () => {
                    this.$state.set(numFailed === 0 ? "loaded" : "error")
                    this.refreshUnassignedJobs()
                    this.notifications.showInfo(
                        `Deleted ${numDeleted} images of configurations.${numFailed > 0 ? ` Failed to delete ${numFailed} images of configurations.` : ""}`,
                    )
                },
            })
    }

    async selectUnassignedJob(job: UnassignedJobData) {
        const refreshedJobQuery = await fetchThrowingErrors(this.jobGql)(job.job)
        const refreshedJob = {...job, job: refreshedJobQuery.job}
        this.$unassignedJobs.update((oldJobs) => oldJobs.map((oldJob) => (oldJob.job.id === job.job.id ? refreshedJob : oldJob)))
        this.$selectedUnassignedJob.set(refreshedJob)
    }

    ngOnDestroy() {
        this.$selectedUnassignedJob.set(undefined)

        this.cancelSubject.next()
        this.cancelSubject.complete()
    }
}
